import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';

import { formatAttributeValueDisplay } from '../../lib/attributeValueDisplay';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import type {
  AttributeDefinition,
  AttributeGroup,
  AttributeValue,
  CatalogLanguage,
  ProductAttribute,
  ProductFamily,
} from '../../model/types';
import { AttributeValueRow } from './AttributeValueRow';

export interface ProductAttributesPanelProps {
  productId: string;
  assignedFamily: ProductFamily | null;
  attributeValues: AttributeValue[];
  /** @deprecated Legacy free-text `{name, value}` pairs — rendered read-only if present. */
  legacyAttributes: ProductAttribute[];
  dictionary: AttributeDefinition[];
  /**
   * The tenant's attribute-group dictionary (plan-catalog-009). Drives the section headers and their
   * order; pass an empty array to render one flat list exactly as before.
   */
  attributeGroups: AttributeGroup[];
  languages: CatalogLanguage[];
  /**
   * The card-language switcher's selected locale (adr-merchant-portal-008) — localizable TEXT
   * rows edit exactly this one language, so the whole card follows a single switcher.
   */
  selectedLocale?: string;
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
}

/**
 * Typed attributes panel — replaces the legacy free-text name/value rows (user-story-merchant-
 * portal-030 slice 2). Driven entirely by the assigned family's attribute list (order =
 * `sortOrder`); with no family assigned, this renders an info state plus any existing values
 * read-only rather than an editor, since there is no attribute list to drive one.
 *
 * Rows are additionally bucketed into **attribute-group sections** (plan-catalog-009): the group
 * order comes from the dictionary's own `sortOrder`, rows keep their family `sortOrder` inside each
 * section, and anything ungrouped falls into a trailing "Other" section. When the tenant has no
 * groups — or every attribute is ungrouped — exactly one unlabelled section renders, so the panel
 * looks and behaves precisely as it did before grouping existed.
 */
export function ProductAttributesPanel({
  productId,
  assignedFamily,
  attributeValues,
  legacyAttributes,
  dictionary,
  attributeGroups,
  languages,
  selectedLocale,
  canManage,
  onNotify,
}: ProductAttributesPanelProps) {
  const { t, i18n } = useTranslation('catalog');

  const rows = useMemo(() => {
    if (!assignedFamily) return [];
    return [...assignedFamily.attributes]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((row) => ({
        row,
        definition: dictionary.find((d) => d.code === row.attributeCode),
        value: attributeValues.find((v) => v.code === row.attributeCode),
      }));
  }, [assignedFamily, dictionary, attributeValues]);

  // Bucket the (already family-ordered) rows into attribute-group sections, ordered by the group
  // dictionary's own sortOrder; ungrouped rows trail in a final unlabelled bucket. Row order inside
  // a section is untouched, so an ungrouped catalog renders exactly the previous flat list.
  const sections = useMemo(() => {
    const groupOrder = new Map(
      [...attributeGroups]
        .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
        .map((g, index) => [g.code, index] as const),
    );
    const buckets = new Map<string, typeof rows>();
    for (const entry of rows) {
      // The row's group comes from its dictionary definition, not the family membership.
      const code = entry.definition?.groupCode;
      const key = code && groupOrder.has(code) ? code : '';
      const bucket = buckets.get(key);
      if (bucket) bucket.push(entry);
      else buckets.set(key, [entry]);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => {
        if (a === b) return 0;
        if (a === '') return 1; // ungrouped always last
        if (b === '') return -1;
        return (groupOrder.get(a) ?? 0) - (groupOrder.get(b) ?? 0);
      })
      .map(([code, entries]) => ({ code, entries }));
  }, [rows, attributeGroups]);

  // Typed values whose code isn't part of the currently-assigned family (e.g. left over from a
  // previous family) — rendered read-only rather than dropped silently.
  const orphanedValues = useMemo(() => {
    if (!assignedFamily) return attributeValues;
    const familyCodes = new Set(assignedFamily.attributes.map((a) => a.attributeCode));
    return attributeValues.filter((v) => !familyCodes.has(v.code));
  }, [assignedFamily, attributeValues]);

  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = languages.find((l) => l.defaultLocale)?.locale;

  const orphanedLabel = (value: AttributeValue): string => {
    const def = dictionary.find((d) => d.code === value.code);
    return def
      ? resolveLocalizedText(def.translations, 'label', i18n.language, def.code, defaultLocale)
      : value.code;
  };

  const groupLabel = (code: string): string | null => {
    if (!code) return null;
    const group = attributeGroups.find((g) => g.code === code);
    return group
      ? resolveLocalizedText(group.translations, 'label', i18n.language, group.code, defaultLocale)
      : code;
  };

  const hasLegacyOrOrphaned = legacyAttributes.length > 0 || orphanedValues.length > 0;

  return (
    <Stack spacing={2}>
      {!assignedFamily ? <Alert severity="info">{t('productAttributesPanel.noFamilyNotice')}</Alert> : null}

      {assignedFamily ? (
        rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('productAttributesPanel.familyHasNoAttributes')}
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {sections.map(({ code, entries }) => {
              const label = groupLabel(code);
              return (
                <Stack key={code || '__ungrouped__'} spacing={1.5}>
                  {/* A single unlabelled section (no groups configured at all) renders no header, so
                      an ungrouped catalog is visually identical to the pre-grouping panel. */}
                  {label || (code === '' && sections.length > 1) ? (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: '0.08em', lineHeight: 1.6 }}
                    >
                      {label ?? t('productAttributesPanel.ungroupedSection')}
                    </Typography>
                  ) : null}
                  <Stack spacing={1.5}>
            {entries.map(({ row, definition, value }) =>
              definition ? (
                <AttributeValueRow
                  key={row.attributeCode}
                  productId={productId}
                  definition={definition}
                  required={row.required}
                  value={value}
                  languages={languages}
                  selectedLocale={selectedLocale}
                  canManage={canManage}
                  onNotify={onNotify}
                />
              ) : (
                // Handle gracefully: the family references a code no longer in the dictionary.
                <Box key={row.attributeCode} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('productAttributesPanel.definitionMissing', { code: row.attributeCode })}
                  </Typography>
                </Box>
              ),
            )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )
      ) : null}

      {hasLegacyOrOrphaned ? (
        <>
          <Divider />
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
              {t('productAttributesPanel.legacySection.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('productAttributesPanel.legacySection.description')}
            </Typography>
            <Stack spacing={0.75}>
              {orphanedValues.map((value) => (
                <Stack key={value.code} direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>
                    {orphanedLabel(value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatAttributeValueDisplay(value)}
                  </Typography>
                </Stack>
              ))}
              {legacyAttributes.map((attr, index) => (
                <Stack key={`${attr.name}-${index}`} direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>
                    {attr.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {attr.value}
                  </Typography>
                  <Chip size="small" variant="outlined" label={t('productAttributesPanel.legacyChip')} />
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      ) : null}

      {!assignedFamily && !hasLegacyOrOrphaned ? (
        <Typography variant="body2" color="text.secondary">
          {t('productEdit.noAttributes')}
        </Typography>
      ) : null}
    </Stack>
  );
}
